import React, { useState, useRef, useCallback, useEffect } from 'react'
import { arrayOf, bool, func, instanceOf, number, oneOfType, string } from 'prop-types'

import { useHotspots } from '../../gate/hotspots-provider.js'
import Locale from '../../../../models/Locale.js'
import getOptimisedImagesUrl, {
  formatSizes,
  getSrcSetUrls,
} from '../../../utilities/getOptimisedImagesUrl.js'
import getImagePaddingBottom from '../../../utilities/getImagePaddingBottom.js'
import getPostAltText from '../../../utilities/getPostAltText.js'
import Post from '../../../../models/Post.js'
import Marker from './post-hotspot-marker.js'

export const getContainerCoordinates = (x, y, imageSize, offsets) => {
  const [imageWidth, imageHeight] = imageSize
  const absoluteX = x * imageWidth
  const absoluteY = y * imageHeight

  const [widthOffset, heightOffset] = offsets
  const containerX = absoluteX + widthOffset
  const containerY = absoluteY + heightOffset
  return [containerX, containerY]
}

const { min, round } = Math

function PostImage({
  isLightBox = false,
  decoding = false,
  lazy = true,
  locale = null,
  post,
  sizes = 0,
  onLoad = () => null,
  className,
  srcSets = [240, 320, 480, 640],
  mediaIndex = 0,
}) {
  const media = post.getMediaFromIndex(mediaIndex)
  const altText = getPostAltText(post, locale)
  const postStyling = post
  const mediaRef = useRef(null)
  const [naturalSize, setNaturalSize] = useState([0, 0])
  const [imageSize, setImageSize] = useState([0, 0])
  const [offsets, setOffsets] = useState([0, 0])
  const { fetchHotspots, containerSize, setContainerSize, hotspots, currentProducts } =
    useHotspots()

  const mediaUrl = isLightBox ? media.getThumbnailUrl() : post.thumbnail?.url

  const updateContainerSize = useCallback(() => {
    const container = mediaRef?.current
    if (!container) {
      return
    }
    const { width, height } = container.getBoundingClientRect()
    setContainerSize([width, height])
  }, [mediaRef, setContainerSize])

  useEffect(() => {
    if (isLightBox) {
      fetchHotspots(post)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post])

  useEffect(() => {
    updateContainerSize()

    window.addEventListener('resize', updateContainerSize, { passive: true })
    return () => {
      window.removeEventListener('resize', updateContainerSize)
    }
  }, [mediaRef, updateContainerSize])

  useEffect(() => {
    const [containerWidth, containerHeight] = containerSize
    const [naturalWidth, naturalHeight] = naturalSize

    // Downsize ratio
    const ratio = min(containerHeight / naturalHeight, containerWidth / naturalWidth)
    let imageWidth = naturalWidth
    let imageHeight = naturalHeight
    if (ratio < 1) {
      imageWidth = round(imageWidth * ratio)
      imageHeight = round(imageHeight * ratio)
    }
    setImageSize([imageWidth, imageHeight])
    const widthOffset = round((containerWidth - imageWidth) / 2)
    const heightOffset = round((containerHeight - imageHeight) / 2)
    setOffsets([widthOffset, heightOffset])
  }, [containerSize, naturalSize])

  const onLoadedHandler = (image) => {
    const imageUrl = new URL(image.target.src)
    const hasLoadedCorrectImage = imageUrl.pathname !== null
    if (!hasLoadedCorrectImage) {
      return
    }
    const { naturalWidth, naturalHeight } = image.target
    setNaturalSize([naturalWidth, naturalHeight])
  }

  const renderMarkers = () => {
    if (post.rawMedia.length < 1) {
      return null
    }
    const [containerWidth, containerHeight] = containerSize
    return currentProducts.map(({ key: productKey }, i) => {
      const hotspot = hotspots.find(({ product_key: productId }) => productId === productKey)
      if (!hotspot) {
        return null
      }
      const { product_key: productId, x, y } = hotspot

      if (!productId) {
        return null
      }

      const [containerX, containerY] = getContainerCoordinates(+x, +y, imageSize, offsets)
      const displayedX = containerX / containerWidth
      const displayedY = containerY / containerHeight

      return (
        <Marker
          key={productId}
          container={mediaRef}
          index={i + 1}
          productId={productId}
          x={displayedX}
          y={displayedY}
        />
      )
    })
  }

  if (!media.isValid) {
    return null
  }

  return (
    <React.Fragment>
      <img
        key={`${post.key}-${mediaIndex}`}
        ref={mediaRef}
        alt={altText}
        className={className || 'bzfy-o-post-image'}
        decoding={decoding ? 'auto' : undefined}
        loading={lazy ? 'lazy' : undefined}
        onLoad={(e) => {
          const { target: image } = e.nativeEvent
          const paddingBottom = getImagePaddingBottom(image)
          postStyling.paddingBottom = paddingBottom
          onLoad(image, paddingBottom)
          onLoadedHandler(e)
        }}
        sizes={formatSizes(sizes)}
        srcSet={getSrcSetUrls(mediaUrl, srcSets)}
        // keep this order for Safari to work otherwise it downloads src and srcSet
        // https://bugs.webkit.org/show_bug.cgi?id=190031
        // eslint-disable-next-line react/jsx-sort-props
        src={getOptimisedImagesUrl(mediaUrl, 640)}
      />
      {isLightBox && renderMarkers()}
    </React.Fragment>
  )
}

PostImage.propTypes = {
  className: string,
  decoding: bool,
  isLightBox: bool,
  lazy: bool,
  locale: instanceOf(Locale),
  mediaIndex: number,
  onLoad: func,
  post: instanceOf(Post).isRequired,
  sizes: oneOfType([number, string]),
  srcSets: arrayOf(number),
}

export default PostImage
